import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Play } from 'lucide-react';
import { trackEvent, EventTypes } from '../services/analytics';

// Founder photos
const founderPhotos = {
  aymar: 'https://customer-assets.emergentagent.com/job_indexvec-ai/artifacts/f9keezek_aymar.webp',
  aniss: 'https://customer-assets.emergentagent.com/job_glassmorphic-7/artifacts/p3cro4gg_Capture%20d%E2%80%99e%CC%81cran%202026-01-03%20a%CC%80%2016.06.03.png',
};

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0.5, y: 0.5 });
  const [blobMousePos, setBlobMousePos] = useState({ x: 0, y: 0 });
  const [isHoveringBlob, setIsHoveringBlob] = useState(false);
  const heroRef = useRef(null);
  const blobRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMousePosition({ x, y });

    // Calculate blob-relative position
    if (blobRef.current) {
      const blobRect = blobRef.current.getBoundingClientRect();
      const blobCenterX = blobRect.left + blobRect.width / 2;
      const blobCenterY = blobRect.top + blobRect.height / 2;
      
      // Distance from cursor to blob center, normalized
      const dx = (e.clientX - blobCenterX) / (blobRect.width / 2);
      const dy = (e.clientY - blobCenterY) / (blobRect.height / 2);
      
      setBlobMousePos({ x: dx, y: dy });
      
      // Check if hovering near the blob
      const distance = Math.sqrt(dx * dx + dy * dy);
      setIsHoveringBlob(distance < 1.5);
    }
  }, []);

  const scrollToProcess = () => {
    trackEvent(EventTypes.CTA_CLICK, 'hero_demarrer_projet', 'landing');
    document.querySelector('#process')?.scrollIntoView({ behavior: 'smooth' });
  };

  const goToProjects = () => {
    trackEvent(EventTypes.CTA_CLICK, 'hero_voir_realisations', 'landing');
    navigate('/projets');
  };

  const scrollToContact = () => {
    trackEvent(EventTypes.CTA_CLICK, 'hero_demarrer_projet', 'landing');
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      ref={heroRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0b]"
    >
      {/* Animated Gradient Background */}
      <div className="absolute inset-0">
        {/* Main gradient orbs */}
        <div 
          className="absolute w-[800px] h-[800px] rounded-full blur-[120px] opacity-30"
          style={{
            background: 'radial-gradient(circle, #3dd3ee 0%, transparent 70%)',
            left: `${20 + mousePosition.x * 10}%`,
            top: `${10 + mousePosition.y * 10}%`,
            transition: 'left 0.3s ease-out, top 0.3s ease-out',
          }}
        />
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-[100px] opacity-20"
          style={{
            background: 'radial-gradient(circle, #fad24b 0%, transparent 70%)',
            right: `${10 + mousePosition.x * 5}%`,
            bottom: `${20 + mousePosition.y * 5}%`,
            transition: 'right 0.3s ease-out, bottom 0.3s ease-out',
          }}
        />
        <div 
          className="absolute w-[500px] h-[500px] rounded-full blur-[80px] opacity-15"
          style={{
            background: 'radial-gradient(circle, #c8aff0 0%, transparent 70%)',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
        
        {/* Radial gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0b]/50 to-[#0a0a0b]" />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full animate-float-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${8 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 mb-8">
              <div className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#3dd3ee] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#3dd3ee]"></span>
              </div>
              <span className="text-sm font-medium text-white/70">Experts IA & Développement produit</span>
            </div>

            {/* Main Title */}
            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] mb-8">
              <span className="text-white">Transformez vos</span>
              <br />
              <span className="relative inline-block">
                <span 
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: 'linear-gradient(135deg, #3dd3ee 0%, #c8aff0 50%, #fad24b 100%)',
                  }}
                >
                  idées IA
                </span>
                <svg className="absolute -bottom-2 left-0 w-full h-3" viewBox="0 0 200 12" preserveAspectRatio="none">
                  <path 
                    d="M0,8 Q50,0 100,8 T200,8" 
                    stroke="url(#gradient)" 
                    strokeWidth="3" 
                    fill="none" 
                    className="animate-draw"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3dd3ee" />
                      <stop offset="100%" stopColor="#fad24b" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
              <br />
              <span className="text-white">en production</span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-white/60 mb-10 max-w-xl leading-relaxed">
              Solutions IA <span className="text-white/90 font-medium">déployées</span>, 
              mesurées par des <span className="text-white/90 font-medium">KPI</span> et 
              adoptées par vos <span className="text-white/90 font-medium">équipes</span>.
              Pas de POC sans lendemain.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-12">
              <Button
                onClick={scrollToProcess}
                className="group relative bg-gradient-to-r from-[#3dd3ee] via-[#6ee7f0] to-[#c8aff0] text-[#0a0a0b] font-semibold px-8 py-6 text-lg transition-all duration-500 hover:scale-105 hover:shadow-[0_0_50px_rgba(61,211,238,0.4)] overflow-hidden rounded-xl"
              >
                {/* Animated shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative z-10 flex items-center">
                  Démarrer un projet
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
              <Button
                variant="outline"
                onClick={goToProjects}
                className="group relative px-8 py-6 text-lg bg-white/5 border border-white/20 text-white hover:bg-white/10 hover:border-[#3dd3ee]/50 transition-all duration-300 rounded-xl backdrop-blur-sm overflow-hidden"
              >
                {/* Gradient border on hover */}
                <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'linear-gradient(135deg, rgba(61,211,238,0.1), rgba(200,175,240,0.1))' }} />
                <Play className="relative z-10 mr-2 w-5 h-5 group-hover:scale-110 group-hover:text-[#3dd3ee] transition-all" />
                <span className="relative z-10">Voir nos réalisations</span>
              </Button>
            </div>

            {/* Founders Avatars - Teams style */}
            <div className="flex items-center gap-4">
              <div className="flex -space-x-4">
                {/* Aymar */}
                <div className="relative">
                  <div 
                    className="w-14 h-14 rounded-full border-3 border-[#0a0a0b] bg-cover bg-center ring-2 ring-[#3dd3ee]/50"
                    style={{ backgroundImage: `url(${founderPhotos.aymar})` }}
                  />
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#b4dc19] rounded-full border-2 border-[#0a0a0b]" />
                </div>
                {/* Aniss */}
                <div className="relative">
                  <div 
                    className="w-14 h-14 rounded-full border-3 border-[#0a0a0b] bg-cover bg-center ring-2 ring-[#fad24b]/50 grayscale"
                    style={{ backgroundImage: `url(${founderPhotos.aniss})`, backgroundPosition: 'center 30%' }}
                  />
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#b4dc19] rounded-full border-2 border-[#0a0a0b]" />
                </div>
              </div>
              <div>
                <div className="text-white font-medium text-sm">Aymar & Aniss</div>
                <div className="text-white/40 text-xs">Co-fondateurs IndexCore</div>
              </div>
            </div>
          </div>

          {/* Right Visual - Fluid Technological Shape */}
          <div
            ref={blobRef}
            className={`relative transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div 
              className="relative w-full aspect-square max-w-lg mx-auto"
              style={{
                transform: `translate(${blobMousePos.x * 15}px, ${blobMousePos.y * 15}px)`,
                transition: 'transform 0.3s ease-out',
              }}
            >
              {/* Animated fluid blob */}
              <div 
                className="absolute inset-0 flex items-center justify-center"
                style={{
                  transform: `scale(${isHoveringBlob ? 1.05 + Math.abs(blobMousePos.x * blobMousePos.y) * 0.1 : 1}) 
                              rotate(${blobMousePos.x * 5}deg)
                              skewX(${blobMousePos.x * 3}deg)
                              skewY(${blobMousePos.y * 3}deg)`,
                  transition: 'transform 0.2s ease-out',
                }}
              >
                <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
                  <defs>
                    <linearGradient 
                      id="blobGradient" 
                      x1={`${30 + blobMousePos.x * 20}%`} 
                      y1={`${30 + blobMousePos.y * 20}%`} 
                      x2={`${70 + blobMousePos.x * 20}%`} 
                      y2={`${70 + blobMousePos.y * 20}%`}
                    >
                      <stop offset="0%" stopColor="#3dd3ee">
                        <animate attributeName="stop-color" values="#3dd3ee;#c8aff0;#fad24b;#b4dc19;#3dd3ee" dur="10s" repeatCount="indefinite"/>
                      </stop>
                      <stop offset="50%" stopColor="#c8aff0">
                        <animate attributeName="stop-color" values="#c8aff0;#fad24b;#b4dc19;#3dd3ee;#c8aff0" dur="10s" repeatCount="indefinite"/>
                      </stop>
                      <stop offset="100%" stopColor="#fad24b">
                        <animate attributeName="stop-color" values="#fad24b;#b4dc19;#3dd3ee;#c8aff0;#fad24b" dur="10s" repeatCount="indefinite"/>
                      </stop>
                    </linearGradient>
                    <linearGradient id="blobGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3dd3ee" stopOpacity="0.4">
                        <animate attributeName="stop-color" values="#3dd3ee;#fad24b;#c8aff0;#3dd3ee" dur="8s" repeatCount="indefinite"/>
                      </stop>
                      <stop offset="100%" stopColor="#c8aff0" stopOpacity="0.15">
                        <animate attributeName="stop-color" values="#c8aff0;#3dd3ee;#fad24b;#c8aff0" dur="8s" repeatCount="indefinite"/>
                      </stop>
                    </linearGradient>
                    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="8" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                    <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="15" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {/* Outer glow blob - more deformed */}
                  <g transform="translate(200, 200) scale(1.3)">
                    <path fill="url(#blobGradient2)" filter="url(#softGlow)">
                      <animate
                        attributeName="d"
                        dur="15s"
                        repeatCount="indefinite"
                        values="
                          M120,-157.6C152.7,-141.5,174.4,-102.8,186.5,-62.4C198.6,-22,201.1,20.1,187.7,54.9C174.3,89.7,145,117.2,111.4,143.6C77.8,170,39.9,195.3,-2.9,199.2C-45.7,203.1,-93.5,185.6,-131.7,157.4C-169.9,129.2,-198.5,90.3,-205.7,47.8C-212.9,5.3,-198.6,-40.8,-175.4,-80.2C-152.2,-119.6,-120,-152.3,-83.4,-166.4C-46.8,-180.5,-5.8,-176,30.8,-165.1C67.4,-154.2,87.3,-173.7,120,-157.6Z;
                          M140.5,-182.3C178.5,-156.7,203.3,-112.3,212.8,-66.1C222.3,-19.9,216.5,28.1,197.8,69.4C179.1,110.7,147.5,145.3,108.4,168.3C69.3,191.3,22.7,202.7,-23.7,199.9C-70.1,197.1,-116.3,180.1,-150.8,151.1C-185.3,122.1,-208.1,81.1,-213.3,38.1C-218.5,-4.9,-206.1,-49.9,-183.9,-88.1C-161.7,-126.3,-129.7,-157.7,-92.7,-183.6C-55.7,-209.5,-13.7,-229.9,18.9,-222.1C51.5,-214.3,102.5,-207.9,140.5,-182.3Z;
                          M105.3,-139.7C134.8,-122.2,157.2,-89.4,172.6,-52.4C188,-15.4,196.4,25.8,184.7,60.3C173,94.8,141.2,122.6,105.6,147.3C70,172,30.6,193.6,-11.3,196.8C-53.2,200,-97.6,184.8,-134.4,158.1C-171.2,131.4,-200.4,93.2,-209.1,51.4C-217.8,9.6,-206,-35.8,-183.1,-74.8C-160.2,-113.8,-126.2,-146.4,-88.4,-161.2C-50.6,-176,-9.1,-173,25.5,-163.1C60.1,-153.2,75.8,-157.2,105.3,-139.7Z;
                          M89.4,-122.6C113.1,-104.4,128.6,-76.4,144,-45.8C159.4,-15.2,174.7,18,172.6,51.2C170.5,84.4,151,117.6,122.5,140.8C94,164,56.5,177.2,18.5,180.1C-19.5,183,-58,175.6,-92.8,158C-127.6,140.4,-158.7,112.6,-175.4,78C-192.1,43.4,-194.4,2,-184.3,-35.7C-174.2,-73.4,-151.7,-107.4,-121,-130.2C-90.3,-153,-51.4,-164.6,-11.1,-163.9C29.2,-163.2,65.7,-140.8,89.4,-122.6Z;
                          M120,-157.6C152.7,-141.5,174.4,-102.8,186.5,-62.4C198.6,-22,201.1,20.1,187.7,54.9C174.3,89.7,145,117.2,111.4,143.6C77.8,170,39.9,195.3,-2.9,199.2C-45.7,203.1,-93.5,185.6,-131.7,157.4C-169.9,129.2,-198.5,90.3,-205.7,47.8C-212.9,5.3,-198.6,-40.8,-175.4,-80.2C-152.2,-119.6,-120,-152.3,-83.4,-166.4C-46.8,-180.5,-5.8,-176,30.8,-165.1C67.4,-154.2,87.3,-173.7,120,-157.6Z
                        "
                      />
                    </path>
                  </g>
                  
                  {/* Main blob - extreme deformation */}
                  <g transform="translate(200, 200)">
                    <path fill="url(#blobGradient)" filter="url(#glow)">
                      <animate
                        attributeName="d"
                        dur="8s"
                        repeatCount="indefinite"
                        values="
                          M85,-110.5C107.9,-95.9,123.3,-69.3,134.4,-40.6C145.5,-11.9,152.4,19,147.1,50.4C141.8,81.8,124.4,113.7,97.6,130.1C70.8,146.5,34.6,147.4,-0.2,147.6C-35,147.8,-68.5,147.3,-97.3,131.4C-126.1,115.5,-150.2,84.2,-158.6,49.8C-167,15.4,-159.7,-22.1,-143.8,-54.1C-127.9,-86.1,-103.4,-112.6,-75.2,-125.5C-47,-138.4,-15.1,-137.7,10.4,-130.1C35.9,-122.5,62.1,-125.1,85,-110.5Z;
                          M69.3,-92.5C87.8,-77.8,100.1,-55.5,111.4,-30.9C122.7,-6.3,133,20.6,127.8,44.4C122.6,68.2,101.9,88.9,77.8,105.1C53.7,121.3,26.1,133,-4.6,134.9C-35.3,136.8,-68.9,128.9,-97.5,111.5C-126.1,94.1,-149.7,67.2,-157.1,36.8C-164.5,6.4,-155.7,-27.5,-140.1,-56.8C-124.5,-86.1,-102.1,-110.8,-75.9,-124.1C-49.7,-137.4,-19.7,-139.3,4.1,-135.5C27.9,-131.7,50.8,-107.2,69.3,-92.5Z;
                          M98.4,-127C124.6,-109.6,141.5,-77.8,151.8,-44.1C162.1,-10.4,165.8,25.2,155.4,56.3C145,87.4,120.5,114,91.6,130.2C62.7,146.4,29.4,152.2,-5.4,153.1C-40.2,154,-76.4,149.9,-105.3,132.9C-134.2,115.9,-155.8,86,-163.5,53.2C-171.2,20.4,-165,-15.3,-151.1,-47.3C-137.2,-79.3,-115.6,-107.6,-88.2,-124.6C-60.8,-141.6,-27.6,-147.3,3.6,-148.4C34.8,-149.5,72.2,-144.4,98.4,-127Z;
                          M63.4,-87.3C79.9,-71.4,90.3,-50.3,101.8,-26.7C113.3,-3.1,125.9,23,121.6,46.6C117.3,70.2,96.1,91.3,71.5,108.2C46.9,125.1,18.9,137.8,-11.7,141.3C-42.3,144.8,-75.5,139.1,-102.3,121.3C-129.1,103.5,-149.5,73.6,-157.2,41.1C-164.9,8.6,-160,-26.5,-144.7,-55.8C-129.4,-85.1,-103.7,-108.6,-75.5,-123.3C-47.3,-138,-16.6,-143.9,7.5,-139.9C31.6,-135.9,46.9,-103.2,63.4,-87.3Z;
                          M85,-110.5C107.9,-95.9,123.3,-69.3,134.4,-40.6C145.5,-11.9,152.4,19,147.1,50.4C141.8,81.8,124.4,113.7,97.6,130.1C70.8,146.5,34.6,147.4,-0.2,147.6C-35,147.8,-68.5,147.3,-97.3,131.4C-126.1,115.5,-150.2,84.2,-158.6,49.8C-167,15.4,-159.7,-22.1,-143.8,-54.1C-127.9,-86.1,-103.4,-112.6,-75.2,-125.5C-47,-138.4,-15.1,-137.7,10.4,-130.1C35.9,-122.5,62.1,-125.1,85,-110.5Z
                        "
                      />
                    </path>
                  </g>
                  
                  {/* Inner highlight blob */}
                  <g transform="translate(200, 200) scale(0.6)">
                    <path fill="white" fillOpacity="0.1" filter="url(#glow)">
                      <animate
                        attributeName="d"
                        dur="6s"
                        repeatCount="indefinite"
                        values="
                          M54.2,-73.2C68.5,-62.3,77.5,-44.8,82.3,-26.2C87.1,-7.6,87.7,12.1,81.2,29.3C74.7,46.5,61.1,61.2,44.8,72.1C28.5,83,9.5,90.1,-10.8,89.7C-31.1,89.3,-52.7,81.4,-68.2,67.3C-83.7,53.2,-93.1,32.9,-95.2,11.8C-97.3,-9.3,-92.1,-31.2,-80.2,-48.2C-68.3,-65.2,-49.7,-77.3,-31,-83.4C-12.3,-89.5,6.5,-89.6,24,-84.1C41.5,-78.6,39.9,-84.1,54.2,-73.2Z;
                          M43.3,-59.4C55,-48.5,62.8,-34.5,68.4,-18.9C74,-3.3,77.4,13.9,72.8,29.1C68.2,44.3,55.6,57.5,40.8,66.5C26,75.5,9,80.3,-8.6,79.9C-26.2,79.5,-44.4,73.9,-58.2,62.3C-72,50.7,-81.4,33.1,-84.2,14.6C-87,-3.9,-83.2,-23.3,-73.5,-38.9C-63.8,-54.5,-48.2,-66.3,-32.4,-75.8C-16.6,-85.3,-0.6,-92.5,11.5,-88.9C23.6,-85.3,31.6,-70.3,43.3,-59.4Z;
                          M54.2,-73.2C68.5,-62.3,77.5,-44.8,82.3,-26.2C87.1,-7.6,87.7,12.1,81.2,29.3C74.7,46.5,61.1,61.2,44.8,72.1C28.5,83,9.5,90.1,-10.8,89.7C-31.1,89.3,-52.7,81.4,-68.2,67.3C-83.7,53.2,-93.1,32.9,-95.2,11.8C-97.3,-9.3,-92.1,-31.2,-80.2,-48.2C-68.3,-65.2,-49.7,-77.3,-31,-83.4C-12.3,-89.5,6.5,-89.6,24,-84.1C41.5,-78.6,39.9,-84.1,54.2,-73.2Z
                        "
                      />
                    </path>
                  </g>
                </svg>
              </div>
              
              {/* Orbiting dots - react to mouse */}
              <div 
                className="absolute inset-0 animate-spin-slow" 
                style={{ 
                  animationDuration: '20s',
                  transform: `rotate(${blobMousePos.x * 30}deg)`,
                  transition: 'transform 0.5s ease-out',
                }}
              >
                <div className="absolute top-4 left-1/2 -translate-x-1/2">
                  <div 
                    className="w-3 h-3 rounded-full bg-[#3dd3ee]"
                    style={{
                      boxShadow: isHoveringBlob 
                        ? '0 0 30px #3dd3ee, 0 0 60px #3dd3ee' 
                        : '0 0 20px #3dd3ee',
                      transform: `scale(${isHoveringBlob ? 1.5 : 1})`,
                      transition: 'all 0.3s ease-out',
                    }}
                  />
                </div>
              </div>
              <div 
                className="absolute inset-0 animate-spin-slow" 
                style={{ 
                  animationDuration: '30s', 
                  animationDirection: 'reverse',
                  transform: `rotate(${-blobMousePos.y * 30}deg)`,
                  transition: 'transform 0.5s ease-out',
                }}
              >
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                  <div 
                    className="w-2 h-2 rounded-full bg-[#fad24b]"
                    style={{
                      boxShadow: isHoveringBlob 
                        ? '0 0 25px #fad24b, 0 0 50px #fad24b' 
                        : '0 0 15px #fad24b',
                      transform: `scale(${isHoveringBlob ? 1.5 : 1})`,
                      transition: 'all 0.3s ease-out',
                    }}
                  />
                </div>
              </div>
              <div 
                className="absolute inset-0 animate-spin-slow" 
                style={{ 
                  animationDuration: '25s',
                  transform: `rotate(${blobMousePos.x * 20}deg)`,
                  transition: 'transform 0.4s ease-out',
                }}
              >
                <div className="absolute top-1/2 right-4 -translate-y-1/2">
                  <div 
                    className="w-2 h-2 rounded-full bg-[#c8aff0]"
                    style={{
                      boxShadow: isHoveringBlob 
                        ? '0 0 20px #c8aff0, 0 0 40px #c8aff0' 
                        : '0 0 10px #c8aff0',
                      transform: `scale(${isHoveringBlob ? 1.3 : 1})`,
                      transition: 'all 0.3s ease-out',
                    }}
                  />
                </div>
              </div>
              <div 
                className="absolute inset-0 animate-spin-slow" 
                style={{ 
                  animationDuration: '35s', 
                  animationDirection: 'reverse',
                  transform: `rotate(${-blobMousePos.y * 20}deg)`,
                  transition: 'transform 0.4s ease-out',
                }}
              >
                <div className="absolute top-1/2 left-4 -translate-y-1/2">
                  <div 
                    className="w-1.5 h-1.5 rounded-full bg-[#b4dc19]"
                    style={{
                      boxShadow: isHoveringBlob 
                        ? '0 0 15px #b4dc19, 0 0 30px #b4dc19' 
                        : '0 0 10px #b4dc19',
                      transform: `scale(${isHoveringBlob ? 1.3 : 1})`,
                      transition: 'all 0.3s ease-out',
                    }}
                  />
                </div>
              </div>
              
              {/* Mouse follower glow */}
              {isHoveringBlob && (
                <div 
                  className="absolute w-32 h-32 rounded-full pointer-events-none"
                  style={{
                    background: 'radial-gradient(circle, rgba(61,211,238,0.3) 0%, transparent 70%)',
                    left: `calc(50% + ${blobMousePos.x * 100}px)`,
                    top: `calc(50% + ${blobMousePos.y * 100}px)`,
                    transform: 'translate(-50%, -50%)',
                    transition: 'left 0.1s ease-out, top 0.1s ease-out',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/40 rounded-full animate-scroll" />
        </div>
      </div>

      {/* Custom CSS */}
      <style>{`
        @keyframes float-particle {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100vh) translateX(20px);
            opacity: 0;
          }
        }
        
        .animate-float-particle {
          animation: float-particle 10s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Hero;
